import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { formatErrorMessage } from '../utils/errorHandler';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect URL - nereden geldiyse oraya dön
  const from = location.state?.from?.pathname || null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(email, password);
      toast.success('Giriş başarılı!');
      
      // State'in güncellenmesi için kısa bir bekleme
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Redirect mantığı: from varsa oraya, yoksa role'e göre
      if (from) {
        navigate(from);
      } else if (userData?.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/signals');
      }
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Giriş başarısız'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-dark flex items-start justify-center px-4 pt-16 md:pt-24" data-testid="login-page">
      <div className="w-full max-w-md">
        <Card className="border-border/40 shadow-xl" data-testid="login-card">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-heading" data-testid="login-title">Giriş Yap</CardTitle>
            <CardDescription>Hesabınıza giriş yapın</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="ornek@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="input-field"
                  data-testid="login-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Şifre</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Şifrenizi girin"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="input-field pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Link to="/sifremi-unuttum" className="text-sm text-primary hover:underline">
                  Şifremi unuttum
                </Link>
              </div>
              
              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-400">
                Hesabınız yok mu?{' '}
                <Link to="/pricing" className="text-primary hover:underline" data-testid="login-register-link">
                  Kaydol
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoginPage;
